import type { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import { MANIFESTS, getSubItemsForSection } from '@vextura/registry';

export interface NavSubItem {
  id: string;
  label: string;
  /** Absolute href, e.g. `/iam/users`. */
  href: string;
  /** Optional i18n key (shell namespace) — resolves in SideNav via useT. */
  labelKey?: string;
}

export interface NavSection {
  id: string;
  label: string;
  href: string;
  available: boolean;
  /** Optional i18n key (shell namespace). Falls back to `label` when unset. */
  labelKey?: string;
  /**
   * Optional hand-edited sub-items. The registry merges in additional
   * sub-items derived from the section's service.yaml — manifest entries
   * win on id collision. For service sections, prefer authoring the
   * service.yaml and leave subItems empty here.
   */
  subItems?: ReadonlyArray<NavSubItem>;
}

export interface NavGroup {
  label: string;
  /** Optional i18n key (shell namespace). Falls back to `label` when unset. */
  labelKey?: string;
  items: ReadonlyArray<NavSection>;
}

/**
 * BASE navigation tree. Service sections appear as stubs without subItems
 * — the registry fills them in at render time from each section's
 * service.yaml. Add a new service section's stub here once; everything
 * else (sub-items, search corpus, dependency-cruiser allowlist) flows
 * from its manifest.
 *
 * `available: true` is auto-set for any section that has a manifest in
 * the registry. The hand-edited `available` flag below is the fallback
 * for sections without a manifest yet.
 */
export const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    label: 'Platform',
    labelKey: 'nav.groups.platform',
    items: [{ id: 'overview', label: 'Overview', href: '/', available: true, labelKey: 'nav.items.overview' }],
  },
  {
    label: 'Identity & Access',
    labelKey: 'nav.groups.identityAccess',
    items: [
      { id: 'iam', label: 'IAM', href: '/iam', available: false, labelKey: 'nav.items.iam' },
      { id: 'auth', label: 'Auth', href: '/auth', available: false, labelKey: 'nav.items.auth' },
      { id: 'vault', label: 'Vault', href: '/vault', available: false, labelKey: 'nav.items.vault' },
    ],
  },
  {
    label: 'Edge',
    labelKey: 'nav.groups.edge',
    items: [{ id: 'gate', label: 'Gate', href: '/gate', available: false, labelKey: 'nav.items.gate' }],
  },
  {
    label: 'Compute',
    labelKey: 'nav.groups.compute',
    items: [
      { id: 'fn', label: 'Functions', href: '/fn', available: false, labelKey: 'nav.items.fn' },
      {
        id: 'uwf',
        label: 'Workflows',
        href: '/uwf',
        available: false,
        labelKey: 'nav.items.uwf',
        // Hand-authored sub-items merged with manifest-derived Workflows /
        // Executions. Drafts live at /uwf/workflows/drafts (nested under
        // the Workflows list route, not a separate top-level resource) so
        // service.yaml's resource-auto-generator wouldn't emit them.
        subItems: [
          { id: 'uwf-drafts', label: 'Drafts', href: '/uwf/workflows/drafts' },
        ],
      },
    ],
  },
  {
    label: 'Agent',
    labelKey: 'nav.groups.agent',
    items: [{ id: 'mcp', label: 'MCP', href: '/mcp', available: false, labelKey: 'nav.items.mcp' }],
  },
  {
    label: 'Developer',
    labelKey: 'nav.groups.developer',
    items: [
      { id: 'pipeline', label: 'Pipelines', href: '/pipeline', available: false, labelKey: 'nav.items.pipeline' },
      { id: 'storybook', label: 'UI Storybook', href: '/storybook-embed', available: true, labelKey: 'nav.items.storybook' },
    ],
  },
  // Risk group (Antifraud + Incident Manager) moved to standalone consoles
  // — antifraud-console and incident-mgr-console. DocParse likewise lives
  // in doc-parse-console. Removed from the core sidebar so users don't see
  // "coming soon" entries for sections that will never ship here.
  {
    label: 'Settings',
    labelKey: 'nav.groups.settings',
    items: [{ id: 'me', label: 'Profile / Tokens', href: '/me', available: true, labelKey: 'nav.items.me' }],
  },
];

/**
 * Merge base nav with registry-derived sub-items. Any base NavSection
 * whose id matches a service.yaml gets:
 *   - `available: true` (because a registered manifest means the section
 *     is ready)
 *   - `subItems` populated from manifest.resources + manifest.tools
 *     (hand-authored subItems, if any, come first and win on id collision)
 *
 * Cheap — runs once per SideNav render; no IO.
 */
export function mergeRegistry(groups: ReadonlyArray<NavGroup> = NAV_GROUPS): NavGroup[] {
  return groups.map((g) => ({
    // Spread the whole group so labelKey (and any future field) survives.
    // Earlier this rebuilt the group as `{ label, items }` only, which
    // silently dropped labelKey — that's why group HEADERS in the sidebar
    // stayed in English while their child items translated fine.
    ...g,
    items: g.items.map((s) => {
      const manifest = MANIFESTS[s.id];
      if (!manifest) return s;
      const registrySubs = getSubItemsForSection(s.id);
      const handAuthored = s.subItems ?? [];
      const seen = new Set(handAuthored.map((x) => x.id));
      const merged = [
        ...handAuthored,
        ...registrySubs.filter((x) => !seen.has(x.id)),
      ];
      return { ...s, available: true, subItems: merged };
    }),
  }));
}

/**
 * Flat list of every navigable destination across all groups + sub-items,
 * with the registry merged in. Used by Cmd-K search and any other
 * destination-aware UI.
 */
export function flattenedDestinations(): ReadonlyArray<NavSubItem> {
  const out: NavSubItem[] = [];
  for (const g of mergeRegistry()) {
    for (const s of g.items) {
      if (!s.available && !(s.subItems && s.subItems.length)) continue;
      out.push({ id: s.id, label: s.label, href: s.href });
      if (s.subItems) for (const sub of s.subItems) out.push(sub);
    }
  }
  return out;
}

/**
 * Translator callback matching the shape returned by useT('shell'). Passed
 * in from SideNav so this file stays a pure data + shape module; it is not
 * a React component and cannot call hooks itself.
 *
 * Contract: `translate(key, fallback)` — returns the translation for
 * `shell:<key>` if present, otherwise the caller's own `fallback` label.
 * That way missing keys degrade to the English label rather than an ugly
 * "shell:nav.items.foo" render.
 */
export type NavTranslate = (key: string, fallback: string) => string;

export function toCloudscapeItems(
  activePath: string,
  translate: NavTranslate = (_key, fallback) => fallback,
): SideNavigationProps.Item[] {
  const label = (rec: { label: string; labelKey?: string }) =>
    rec.labelKey ? translate(rec.labelKey, rec.label) : rec.label;

  return mergeRegistry().map<SideNavigationProps.Item>((group) => ({
    type: 'section',
    text: label(group),
    items: group.items.map<SideNavigationProps.Item>((item) => {
      if (item.subItems && item.subItems.length) {
        return {
          type: 'expandable-link-group',
          text: label(item),
          href: item.href,
          defaultExpanded: activePath.startsWith(item.href),
          items: item.subItems.map<SideNavigationProps.Item>((sub) => ({
            type: 'link',
            text: label(sub),
            href: sub.href,
          })),
        };
      }
      return {
        type: 'link',
        text: label(item),
        href: item.href,
      };
    }),
  }));
}
